'use client'

import { useLayoutEffect, useState, type RefObject } from 'react'

import {
  getMapHoverCardBoundary,
  resolveMapHoverCardPlacement,
  type MapHoverCardPlacement,
} from '@/components/Location/mapHoverCardPlacement'
import { useMap } from '@/components/ui/map'

export function useMapHoverCardPosition({
  anchorRef,
  cardRef,
  deps = [],
  isActive,
}: {
  anchorRef: RefObject<HTMLElement | null>
  cardRef: RefObject<HTMLElement | null>
  deps?: unknown[]
  isActive: boolean
}): MapHoverCardPlacement | null {
  const { map } = useMap()
  const [placement, setPlacement] = useState<MapHoverCardPlacement | null>(null)

  useLayoutEffect(() => {
    if (!isActive || !anchorRef.current || !cardRef.current) {
      setPlacement(null)
      return
    }

    const updatePlacement = () => {
      const anchor = anchorRef.current
      const card = cardRef.current
      if (!anchor || !card) {
        return
      }

      setPlacement(
        resolveMapHoverCardPlacement({
          anchorRect: anchor.getBoundingClientRect(),
          boundary: getMapHoverCardBoundary(anchor),
          cardRect: card.getBoundingClientRect(),
        }),
      )
    }

    updatePlacement()

    const resizeObserver = new ResizeObserver(updatePlacement)
    resizeObserver.observe(cardRef.current)

    const boundaryElement =
      anchorRef.current.closest('.locations-map-section') ??
      anchorRef.current.closest('[data-locations-map-section]')
    if (boundaryElement) {
      resizeObserver.observe(boundaryElement)
    }

    window.addEventListener('resize', updatePlacement)
    window.addEventListener('scroll', updatePlacement, true)
    map?.on('move', updatePlacement)
    map?.on('zoom', updatePlacement)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updatePlacement)
      window.removeEventListener('scroll', updatePlacement, true)
      map?.off('move', updatePlacement)
      map?.off('zoom', updatePlacement)
    }
  }, [anchorRef, cardRef, isActive, map, ...deps])

  return placement
}
